import { useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Heart } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCreateParentInvite } from "@/hooks/useParentInvites";
import { env } from "@/lib/env";

/**
 * Optional "add a parent" step shown during onboarding (Phase 8). In a
 * student-first design this is the parent portal's primary acquisition
 * funnel, so it plants the invite link at day zero -- but it's genuinely
 * skippable (proceeding with either onboarding CTA simply ignores it). Copy
 * leads with what the student gets and reassures on privacy up front.
 */
export function AddParentStep() {
  const createInvite = useCreateParentInvite();
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [copied, setCopied] = useState(false);

  const onGenerate = async () => {
    try {
      const trimmed = email.trim();
      const result = await createInvite.mutateAsync({ parent_email: trimmed || undefined });
      if (result) {
        setInviteUrl(result.invite_url);
        setInviteCode(result.invite_code);
        if (trimmed) toast.success("Invite emailed -- and here's a link you can share too.");
      }
    } catch (err) {
      const message =
        (err as { response?: { data?: { status_message?: string } } }).response?.data?.status_message ||
        "Couldn't create an invite. You can add a parent later in Settings.";
      toast.error(message);
    }
  };

  const onCopy = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      toast.success("Invite link copied");
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy -- select and copy the link manually");
    }
  };

  const shareUrl = inviteUrl ?? `${env.VITE_APP_ORIGIN.replace(/\/+$/, "")}/auth/signup`;

  return (
    <Card className="p-5 sm:p-6 flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-lg bg-brand-soft text-brand flex-shrink-0">
          <Heart className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm font-medium">Add a parent (optional)</p>
          <p className="text-xs text-text-muted mt-0.5">
            They get progress updates and can cheer you on -- they never see your chats. You can skip this and add
            them later.
          </p>
        </div>
      </div>

      {inviteCode ? (
        <div className="rounded-lg border border-brand/30 bg-brand-soft/50 p-3">
          <p className="text-xs text-text-muted">Share this link or code with your parent:</p>
          <p className="mt-1 font-mono text-lg tracking-widest">{inviteCode}</p>
          <div className="mt-2 flex items-center gap-2">
            <Input readOnly value={shareUrl} className="flex-1 text-xs" onFocus={(e) => e.currentTarget.select()} />
            <Button type="button" variant="secondary" size="sm" onClick={onCopy}>
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Parent's email (optional)"
            className="flex-1"
          />
          <Button type="button" variant="secondary" loading={createInvite.isPending} onClick={onGenerate}>
            {email.trim() ? "Send invite" : "Get a share link"}
          </Button>
        </div>
      )}
    </Card>
  );
}
